<template>
  <div class="main-container-fluid goals-wrapper align-self-center w-100">
    <div
      class="col-12 col-md-10 col-lg-10 goals-container align-self-center"
      ref="listContainer"
      :key="goalsContainerKey"
    >
      <template>
        <div class="loaders" v-if="goalsToBeShown === null">
          <goal-skeleton />
          <goal-skeleton />
          <goal-skeleton />
          <goal-skeleton />
        </div>
        <timeline-wise-goals
          class="mb-4"
          :selected-timeline="selectedTimeline"
          :goals="goalsToBeShown"
          :org-id="orgId"
          :source="source"
          @delete-goal="$emit('delete-goal')"
          @archive-goal="$emit('archive-goal')"
          @arrange="onArrange"
          @move-goal="refreshGoals"
          v-else-if="goalsToBeShown.length > 0"
        />
        <div v-else class="row justify-content-center" style="height: 400px;">
          <info-box>
            <template slot="message" v-no-capture>
              {{ team.name }} does not have any goals in the selected timeline({{
                selectedTimeline.name
              }}). But when it does, this is where you’ll see them.
            </template>
            <add-goal-actions :org-id="orgId" :team-id="teamId" />
          </info-box>
        </div>
      </template>
      <bulk-check-in
        @checkin-posted="refreshGoals"
        v-if="isBulkCheckinModalShown"
        v-model="isBulkCheckinModalShown"
        :org-id="orgId"
        :team-id="teamId"
      />
      <bulk-action-tray />
    </div>
  </div>
</template>

<script>
import BulkActionTray from '@/components/bulk-actions/BulkActionTray.vue'
import BulkCheckIn from '@/components/BulkCheckIn.vue'
import AddGoalActions from '@/components/goal/AddGoalActions.vue'
import TimelineWiseGoals from '@/components/goal/TimelineWiseGoals'
import InfoBox from '@/components/InfoBox'
import GoalSkeleton from '@/components/skeletons/GoalSkeleton'
import { SOURCE } from '@/constants/analytics'
import events from '@/constants/events'
import BulkActionsMixin from '@/mixins/BulkActionsMixin'
import DragAndDropMixin from '@/mixins/DragAndDropMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import * as keymapService from '@/services/keymapService/keymapService'
import debounce from '@/utils/debounce'
import selectFirstItem from '@/utils/selectFirstItem'
import unselectItem from '@/utils/unselectItem'
import GoalsMixin from '@/views/GoalsMixin'

/**
 * This component show goals for team, org
 */
export default {
  name: 'TeamGoals',
  props: ['orgId', 'teamId', 'interval', 'selectedTimeline', 'query'],
  components: {
    AddGoalActions,
    BulkCheckIn,
    GoalSkeleton,
    InfoBox,
    TimelineWiseGoals,
    BulkActionTray
  },
  mixins: [GoalsMixin, DragAndDropMixin, EventBusMixin, BulkActionsMixin],
  data() {
    return {
      goals: null,
      goalsContainerKey: 1,
      source: SOURCE.TEAM_PAGE,
      isBulkCheckinModalShown: false
    }
  },
  computed: {
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    goalsToBeShown() {
      if (!this.goals) return null
      else {
        return this.goals.filter(
          goal => !goal.parentGoalIds.some(goalId => this.goals.some(goal => goal.id === goalId))
        )
      }
    }
  },
  methods: {
    async refreshGoals() {
      this.goals = await this.$store.dispatch('getGoals', {
        orgId: this.orgId,
        teamId: this.teamId,
        options: {
          intervalStart: this.selectedTimeline.start,
          intervalEnd: this.selectedTimeline.end,
          intervalSubset: this.selectedTimeline.subset,
          isArchived: this.team ? this.team.isArchived : false,
          expand: 'parent_goal_ids'
        }
      })
    },
    refreshPageData: debounce(async function() {
      await this.refreshGoals()
      this.$nextTick(function() {
        unselectItem(this.$refs.listContainer)
        selectFirstItem(this.$refs.listContainer)
      })
    }, 100),
    showBulkCheckinModal() {
      this.isBulkCheckinModalShown = true
    },
    initializeKeyMapService() {
      keymapService.activateCategory('goals', {
        'previous-item': this.selectPreviousItem.bind(this),
        'next-item': this.selectNextItem.bind(this),
        'expand-item': this.expandItem.bind(this),
        'collapse-item': this.collapseItem.bind(this),
        'toggle-expansion': () => this.toggleExpansion(),
        open: this.viewGoal,
        'view-goal': this.viewGoal.bind(this),
        'edit-item': this.editItem.bind(this),
        checkin: this.handleSpacebar.bind(this),
        'cancel-editing': this.cancelEditing,
        'add-initiative': this.addInitiative,
        comment: this.comment,
        'delete-initiative': this.triggerDeleteInitiative,
        'bulk-checkin': this.showBulkCheckinModal
      })
    },
    onArrange({ subjectId, beforeId }) {
      this.goals = this.shiftItem(this.goals, subjectId, beforeId)
    }
  },
  mounted() {
    this.refreshPageData()
    this.initializeKeyMapService()
    this.listenToEventBus(events.TEAM_ARCHIVED_UNARCHIVED, this.refreshGoals)
    this.listenToEventBus(events.PARENT_GOAL_ALIGNMENT_CHANGED, this.refreshGoals)
    if (!this.team.preferences.enableGoals) {
      this.eventBusEmit(events.SHOW_404)
    }
  },
  beforeDestroy() {
    keymapService.deactivateCategory('goals')
  },
  watch: {
    orgId: 'refreshPageData',
    type: 'refreshPageData',
    teamId: 'refreshPageData',
    selectedTimeline: 'refreshPageData'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/_drag.scss';

.main-container-fluid {
  margin: 0 auto;
  width: 100%;
}

.goals-wrapper {
  display: flex;
  justify-content: center;
  width: inherit;
  flex-shrink: 0;
}

.goals-container {
  padding: 0;
  display: flex;
  flex-direction: column;
}

.loaders {
  padding-left: 62px;
}

/deep/ .goal-skeleton {
  border-top: 1px solid $borderSecondary;
  padding-left: 27px;
}

.draggable-item {
  transition: opacity ease-in 200ms;
}

@include media-breakpoint-down(md) {
  .goals-container {
    padding: 0 15px;
  }
  .goals-wrapper /deep/ .goal-container {
    padding: 0;
  }
  /deep/ .goal-skeleton {
    padding-left: 0;
  }
  .loaders {
    padding-left: 15px;
  }
}

@include media-breakpoint-up(lg) {
  .goals .goal-container {
    margin-left: -16px;
    margin-right: -16px;
  }
}

@include media-breakpoint-down(sm) {
  .goals-container {
    padding: 0;
  }
}
</style>

<style lang="scss">
.goal-container.gu-mirror .initiative-row .gripper,
.goal-container.gu-mirror .kr .gripper {
  display: none !important;
}

.goals-wrapper {
  .drag-active {
    .goal-container:hover .gripper {
      display: none;
    }
  }
}
</style>
